import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Map } from 'lucide-react';
import MapComponent from './MapComponent';
import { useSelector } from 'react-redux';
import { selectUserType } from '../store/slices/authSlice';

const MapModal = ({ isOpen, onClose, stops }) => {
  const userType = useSelector(selectUserType);
  const isCarrier = ['carrier', 'carrier_admin'].includes(userType);

  console.log(stops[0].state);
  
  // Transform stops into the format expected by MapComponent
  const locations = stops.map((stop, index) => {
    // Debug log to see exact values being used
    console.log('Stop data:', {
      city: stop.city,
      state: stop.state,
      address: stop.address
    });

    return {
      id: stop.id,
      location: {
        latitude: stop.coordinates?.latitude,
        longitude: stop.coordinates?.longitude,
        city: stop.city,
        state: stop.state,
        address: stop.address,
      },
      timestamp: stop.date && stop.time ? (() => {
        const [datePart] = stop.date.split('T');
        const combinedDateTime = `${datePart}T${stop.time}`;
        return new Date(combinedDateTime).toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      })() : null,
      is_authenticated: true,
      stop_type: stop.stop_type,
      sequence: stop.sequence,
      business_name: stop.business_name,
      contact_name: stop.contact_name,
      contact_phone: stop.contact_phone,
      contact_instructions: stop.contact_instructions
    };
  });

  const stats = {
    total: stops.length,
    pickups: stops.filter(stop => stop.stop_type === 'pickup').length,
    deliveries: stops.filter(stop => stop.stop_type === 'delivery').length
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 overflow-y-auto"
        >
          <div className="flex items-center justify-center min-h-screen text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={onClose}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="inline-block w-full h-full sm:h-auto sm:max-w-6xl overflow-hidden align-bottom bg-white rounded-lg shadow-xl transform transition-all"
            >
              {/* Mobile-optimized Header */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-white">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4">
                  {/* Left side - Title and Stats */}
                  <div className="flex items-start gap-3 flex-grow">
                    <div className="p-2 bg-indigo-50 rounded-lg hidden sm:block">
                      <Map className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Route Overview</h3>
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 text-xs sm:text-sm font-medium">
                          {stats.total} Stops
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs sm:text-sm font-medium">
                          {stats.pickups} Pickups
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs sm:text-sm font-medium">
                          {stats.deliveries} Deliveries
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={onClose}
                    className="absolute right-2 top-2 sm:static p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-50 rounded-lg transition-colors"
                    aria-label="Close modal"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Map Container - Adjusted height for mobile */}
              <div className="h-[calc(100vh-10rem)] sm:h-[600px] w-full">
                <MapComponent
                  locations={locations}
                  clustered={false}
                  showInfo={true}
                  isLoadStop={true}
                  isCarrier={isCarrier}
                />
              </div>

              {/* Mobile-optimized Legend */}
              <div className="absolute bottom-4 left-4 z-[1000] bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-2.5 space-y-1.5 text-xs sm:text-sm max-w-[150px] sm:max-w-none">
                <div className="font-medium text-gray-700">Route Legend</div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                  <span className="text-gray-600">Pickup</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                  <span className="text-gray-600">Delivery</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-0.5 bg-indigo-600"></div>
                  <span className="text-gray-600">Route</span>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MapModal; 